import React, { useState } from "react";
import { useAgencyData } from "@/hooks/useAgencyData";
import ScrollArea from "@/components/ui/shared/ScrollArea";
import Table from "@/components/ui/shared/table/Table";
import TableHeader from "@/components/ui/shared/table/TableHeader";
import TableBody from "@/components/ui/shared/table/TableBody";
import TableRow from "@/components/ui/shared/table/TableRow";
import TableCell from "@/components/ui/shared/table/TableCell";
import { format } from "date-fns";
import {
  Plus,
  ShoppingCart,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Pencil,
} from "lucide-react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import EditSubaccountModal from "./EditSubaccountModal";
import {
  validateAccountName,
  validateLicenseCode,
} from "@/lib/validators/subaccount-validators";
import type { Database } from "@/lib/supabase";

type Account = Database["public"]["Tables"]["accounts"]["Row"];

interface ValidationState {
  status: "idle" | "validating" | "valid" | "invalid";
  message: string;
}

const AgencySubaccounts: React.FC = () => {
  const { accounts, loading, error, refetchAccounts, updateAccount } = useAgencyData();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [formData, setFormData] = useState({
    license_code: "",
    nome: "",
  });

  // Estados de validação
  const [validations, setValidations] = useState<{
    nome: ValidationState;
    license_code: ValidationState;
  }>({
    nome: { status: "idle", message: "" },
    license_code: { status: "idle", message: "" },
  });

  const filteredAccounts = accounts.filter((account) =>
    account.nome.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Resetar validação quando usuário começa a digitar
    setValidations((prev) => ({
      ...prev,
      [field]: { status: "idle", message: "" },
    }));
  };

  const validateField = (field: string, value: string) => {
    let result;

    switch (field) {
      case "nome":
        result = validateAccountName(value);
        break;
      case "license_code":
        result = validateLicenseCode(value);
        break;
      default:
        return;
    }

    setValidations((prev) => ({
      ...prev,
      [field]: {
        status: result.isValid ? "valid" : "invalid",
        message: result.message,
      },
    }));
  };

  const handleBlur = (field: string) => {
    const value = formData[field as keyof typeof formData];
    if (value) {
      validateField(field, value);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const nomeValidation = validateAccountName(formData.nome);
    const licenseValidation = validateLicenseCode(formData.license_code);

    setValidations({
      nome: {
        status: nomeValidation.isValid ? "valid" : "invalid",
        message: nomeValidation.message,
      },
      license_code: {
        status: licenseValidation.isValid ? "valid" : "invalid",
        message: licenseValidation.message,
      },
    });

    if (!nomeValidation.isValid || !licenseValidation.isValid) {
      toast.error("Por favor, corrija os erros no formulário");
      return;
    }

    setSaving(true);
    const toastId = toast.loading("Criando subconta...");

    try {
      const agencyId = user?.account?.agency_id || user?.agency_id;

      if (!agencyId) {
        throw new Error("Agência não encontrada");
      }

      // A subconta nasce com o próprio usuário da agência como dono: é ele quem
      // opera a conta até que o cliente seja adicionado pela tela de Equipe.
      const ownerEmail = user?.email;

      if (!ownerEmail) {
        throw new Error("Não foi possível identificar o e-mail do seu usuário");
      }

      // Chamar edge function para criar subconta completa
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-subaccount-with-owner`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            agency_id: agencyId,
            license_code: formData.license_code,
            nome: formData.nome,
            email: ownerEmail,
          }),
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Erro ao criar subconta");
      }

      toast.dismiss(toastId);
      toast.success("Subconta criada com sucesso!");

      setDialogOpen(false);

      setFormData({
        license_code: "",
        nome: "",
      });
      setValidations({
        nome: { status: "idle", message: "" },
        license_code: { status: "idle", message: "" },
      });

      await refetchAccounts();
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err instanceof Error ? err.message : "Erro ao criar conta");
      console.error("Error creating subaccount:", err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (accountId: string, nome: string): Promise<boolean> => {
    const toastId = toast.loading("Salvando alterações...");
    const { success, error: updateError } = await updateAccount(accountId, { nome });
    toast.dismiss(toastId);

    if (success) {
      toast.success("Subconta atualizada com sucesso!");
    } else {
      toast.error(updateError || "Erro ao atualizar subconta");
    }

    return success;
  };

  const getValidationIcon = (status: ValidationState["status"]) => {
    if (status === "validating") {
      return <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />;
    }
    if (status === "valid") {
      return <CheckCircle2 className="w-4 h-4 text-green-500" />;
    }
    if (status === "invalid") {
      return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-[#E5E7EB] border-t-[#2563EB] rounded-full animate-spin dark:border-[#3F3F46] dark:border-t-[#60A5FA]"></div>
          <p className="text-sm text-muted-foreground">Carregando subcontas...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">Erro ao carregar subcontas</h3>
          <p className="text-muted-foreground text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full h-full flex flex-col">
        <div className="w-full mt-3 sm:mt-4 flex-wrap gap-3 flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Pesquisar subcontas..."
              className="w-[170px] sm:w-[220px] md:w-[270px] xl:w-[417px] h-11 rounded-xl text-sm placeholder:text-[#94A3B8] text-[#0F172A] border border-[#E5E7EB] bg-white px-3 focus-visible:border-[#2563EB] dark:placeholder:text-[#71717A] dark:text-[#FAFAFA] dark:border-[#3F3F46] dark:bg-[#27272A]"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button className="rounded-xl border border-[#E5E7EB] bg-white min-h-[40px] text-sm text-[#334155] font-medium px-4 flex justify-center items-center gap-2 transition-colors duration-200 hover:bg-[#F1F5F9] dark:border-[#3F3F46] dark:bg-transparent dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]">
              <ShoppingCart className="w-4 h-4" />
              Comprar Licença
            </button>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <button className="rounded-xl bg-[#2563EB] min-h-[40px] text-sm text-white font-medium px-4 flex justify-center items-center gap-2 shadow-sm transition-colors duration-200 hover:bg-[#1D4ED8]">
                  <Plus className="w-4 h-4" />
                  Adicionar novo
                </button>
              </DialogTrigger>
              <DialogContent className="flex w-full max-w-md p-0 rounded-2xl border border-[#E5E7EB] bg-white text-[#0F172A] dark:border-[#3F3F46] dark:bg-[#27272A] dark:text-[#FAFAFA] flex-col">
                <ScrollArea classNames={{ root: "max-h-[90dvh] p-5 overflow-auto" }}>
                  <h3 className="text-xl font-semibold text-foreground mb-6">
                    Adicionar Nova Conta
                  </h3>
                  <form onSubmit={handleSubmit} className="w-full gap-4 flex flex-col">
                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="license_code" className="text-sm font-medium text-foreground">
                        Código da Licença
                      </Label>
                      <div className="relative w-full">
                        <Input
                          id="license_code"
                          value={formData.license_code}
                          onChange={(e) => handleInputChange("license_code", e.target.value)}
                          onBlur={() => handleBlur("license_code")}
                          placeholder="Código da licença"
                          className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] pr-10"
                          required
                          disabled={saving}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {getValidationIcon(validations.license_code.status)}
                        </div>
                      </div>
                      {validations.license_code.status === "invalid" && (
                        <p className="text-red-400 text-sm">{validations.license_code.message}</p>
                      )}
                    </div>

                    <div className="flex flex-col w-full items-start gap-3">
                      <Label htmlFor="nome" className="text-sm font-medium text-foreground">
                        Nome da Conta
                      </Label>
                      <div className="relative w-full">
                        <Input
                          id="nome"
                          value={formData.nome}
                          onChange={(e) => handleInputChange("nome", e.target.value)}
                          onBlur={() => handleBlur("nome")}
                          placeholder="Nome da conta"
                          className="h-11 rounded-xl border border-[#E5E7EB] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] dark:border-[#3F3F46] dark:bg-[#18181B] dark:text-[#FAFAFA] dark:placeholder:text-[#71717A] pr-10"
                          required
                          disabled={saving}
                        />
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          {getValidationIcon(validations.nome.status)}
                        </div>
                      </div>
                      {validations.nome.status === "invalid" && (
                        <p className="text-red-400 text-sm">{validations.nome.message}</p>
                      )}
                    </div>

                    <p className="text-muted-foreground text-sm">
                      A conta será criada com o seu usuário como responsável. Para dar acesso
                      ao cliente, entre na subconta e adicione-o pela tela de Equipe.
                    </p>

                    <div className="w-full flex justify-end gap-3 mt-2">
                      <Button
                        type="button"
                        onClick={() => setDialogOpen(false)}
                        className="rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] dark:border-[#3F3F46] dark:bg-transparent dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
                        disabled={saving}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        className="rounded-xl bg-[#2563EB] text-white hover:bg-[#1D4ED8] transition-colors duration-200"
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Criando...
                          </>
                        ) : (
                          "Adicionar"
                        )}
                      </Button>
                    </div>
                  </form>
                </ScrollArea>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {filteredAccounts.length === 0 ? (
          <div className="flex-1 min-h-0">
            <div className="w-full h-full flex items-center justify-center border border-border rounded-[20px] bg-card shadow-sm"
              style={{ height: "calc(100vh - 300px)" }}
            >
              <div className="flex flex-col items-center gap-4 text-center p-6">
                <div className="w-16 h-16 bg-card-muted rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {searchTerm ? "Nenhuma subconta encontrada" : "Nenhuma subconta cadastrada"}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {searchTerm
                      ? "Tente ajustar sua busca"
                      : "Adicione uma nova conta para começar"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 min-h-0">
            <div
              className="w-full max-h-full overflow-auto border border-border rounded-[20px] bg-card shadow-sm table-scrollbar"
              style={{ height: "calc(100vh - 300px)" }}
            >
              <Table className="min-w-[600px]">
                <TableHeader className="border-b border-border sticky top-0 bg-card z-10">
                  <TableRow>
                    {["Nome", "Licença", "Data de Criação", "Ações"].map((item) => (
                      <TableCell
                        key={item}
                        as="th"
                        className={`!text-[#475569] !font-semibold !text-[13px] !py-4 ${item === "Ações" ? "!text-right" : ""}`}
                      >
                        <div className={`flex items-center gap-1 ${item === "Ações" ? "justify-end" : "justify-start"}`}>
                          <p>{item}</p>
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAccounts.map((account) => (
                    <TableRow
                      key={account.id}
                      className="hover:bg-[#F8FAFC] dark:hover:bg-hover transition-colors duration-200 border-b border-border/60 last:border-b-0"
                    >
                      <TableCell as="td" className="!py-4">
                        <p className="font-semibold text-foreground">{account.nome}</p>
                      </TableCell>
                      <TableCell as="td" className="!py-4 !text-[#334155] dark:!text-foreground">
                        <span className="font-mono text-sm">
                          {account.license_id ? account.license_id.slice(0, 8) : "-"}
                        </span>
                      </TableCell>
                      <TableCell as="td" className="!py-4 !text-[#334155] dark:!text-foreground">
                        {account.created_at
                          ? format(new Date(account.created_at), "dd/MM/yyyy HH:mm")
                          : "-"}
                      </TableCell>
                      <TableCell as="td" className="!py-4">
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => setEditingAccount(account)}
                            title="Editar nome"
                            aria-label={`Editar ${account.nome}`}
                            className="w-8 h-8 flex items-center justify-center rounded-xl border border-[#E5E7EB] bg-white text-[#334155] hover:bg-[#F1F5F9] transition-colors dark:border-[#3F3F46] dark:bg-transparent dark:text-[#D4D4D8] dark:hover:bg-[#3F3F46]"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      <EditSubaccountModal
        account={editingAccount}
        onClose={() => setEditingAccount(null)}
        onSave={handleSaveEdit}
      />
    </>
  );
};

export default AgencySubaccounts;
